import { z } from "zod";

// URL state schema for the request list. Defaults are stripped from the serialized URL so
// shared links stay clean. Unknown values are dropped silently with a console.warn.

const slownessBucket = z.enum([
  "OnTrack",
  "Slow",
  "Critical",
  "InsufficientData",
  "InProgress",
]);
const status = z.enum([
  "Unknown",
  "Completed",
  "InProgress",
  "Draft",
  "PendingValidation",
  "RequiresReview",
  "PendingOnboarding",
  "OnHold",
  "Stalled",
  "AwaitingConsultant",
  "Discarded",
  "Cancelled",
]);
const sortDir = z.enum(["asc", "desc"]);
const quickFilter = z.enum(["slow", "inprogress"]);
const pageSize = z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)]);
const tab = z.enum(["aggregate", "instances"]);
const groupByKey = z.enum([
  "client",
  "lob",
  "engagementType",
  "requestType",
  "country",
  "assignedTo",
  "calculatedStatus",
]);
// SP-derived two-bucket projection. The "In Progress" quick filter pushes "InProgress" into
// this array; users have no other UI surface for it today.
const calculatedStatus = z.enum(["Completed", "InProgress"]);

export const requestListSchema = z.object({
  // Tab + aggregate-tab state
  tab: tab.default("aggregate"),
  groupBy: z.array(groupByKey).default(["client"]),
  aggSort: z.string().default("median"),
  aggDir: sortDir.default("desc"),
  minSample: z.number().int().nonnegative().default(1),
  drillKeys: z.array(groupByKey).default([]),
  drillVals: z.array(z.string()).default([]),
  // Existing instances-tab state
  requestType: z.array(z.string()).default([]),
  // Raw Phoenix status filter — no UI surface adds to it; kept for saved-view compatibility and
  // explicit URL access. The visible "Status" everywhere is calculatedStatus (below).
  status: z.array(status).default([]),
  calculatedStatus: z.array(calculatedStatus).default([]),
  client: z.array(z.string()).default([]),
  lob: z.array(z.string()).default([]),
  eng: z.array(z.string()).default([]),
  assigned: z.array(z.string()).default([]),
  country: z.array(z.string()).default([]),
  bucket: z.array(slownessBucket).default([]),
  rework: z.boolean().default(false),
  // Live search text — flows through on every keystroke as the user types (search-as-you-type).
  q: z.string().default(""),
  // Committed search pills — each Enter press in SearchInput pushes a term here and clears `q`.
  // Multiple pills AND together (a row must match every pill) — same as live `q`.
  qPills: z.array(z.string()).default([]),
  // Date window: either dateRange (preset days) or explicit from/to. dateRange wins if both set.
  dateRange: z
    .number()
    .int()
    .positive()
    .default(30),
  from: z.string().optional(),
  to: z.string().optional(),
  sort: z.string().default("slowness"),
  dir: sortDir.default("desc"),
  page: z.number().int().positive().default(1),
  pageSize: pageSize.default(50),
  quickFilter: quickFilter.optional(),
  // Duration unit for the whole screen (instances, tiles, aggregate, slowness). Shared across tabs.
  durationUnit: z.enum(["bd", "cd"]).default("bd"),
  // "Onboarding" bucket: RequestType="Onboarding" with ≥7 non-discarded tasks AND scores slowness
  // against the onboardings-only peer cohort (when redeployment is off). Opt-in (default OFF).
  // Lives inside the Request Type filter group in the UI.
  onboardingsOnly: z.boolean().default(false),
  // "Redeployment" bucket: RequestType="Onboarding" with ≤6 non-discarded tasks, OR raw
  // RequestType="Redeployment" (any task count). ORs with onboardingsOnly (both on = all
  // onboardings + redeployments). Slowness uses the full cohort. Opt-in (default OFF).
  redeployment: z.boolean().default(false),
});

export type RequestListState = z.infer<typeof requestListSchema>;

export const DEFAULTS = requestListSchema.parse({});

export type SerializedRequestListState = Record<string, string | string[]>;
